<template>
  <div class="campaign-picker" ref="root">
    <button
      type="button"
      ref="trigger"
      class="campaign-picker__trigger"
      :aria-label="label"
      aria-haspopup="listbox"
      :aria-expanded="open ? 'true' : 'false'"
      @click="toggle"
    >{{ summary }}</button>

    <!--
      The value the form submits, when this picker is a form field rather than
      part of a filter row. The panel is a control; this is the field, and
      keeping them apart is what lets the control be a scrolling list.
    -->
    <input v-if="name && !multiple" type="hidden" :name="name" :value="chosen[0] || ''">

    <div v-if="open" class="campaign-picker__panel">
      <input
        ref="search"
        v-model="campaigns.search"
        type="search"
        class="campaign-picker__search"
        placeholder="Search your campaigns"
        aria-label="Search your campaigns"
      >

      <p v-if="campaigns.error && !campaigns.items.length" class="campaign-picker__note">{{ campaigns.error }}</p>
      <p v-else-if="campaigns.loading && !campaigns.items.length" class="campaign-picker__note">Loading&hellip;</p>
      <p v-else-if="campaigns.isEmpty" class="campaign-picker__note">
        {{ campaigns.search ? 'No campaign by that name.' : 'You have no campaigns yet.' }}
      </p>

      <ul v-else :ref="campaigns.observe" class="campaign-picker__list" role="listbox" :aria-multiselectable="multiple">
        <li
          v-for="(option, index) in campaigns.items"
          :key="option.id"
          role="presentation"
          :data-load-more="index === campaigns.triggerIndex ? '' : null"
        >
          <button
            type="button"
            role="option"
            :aria-selected="isChosen(option) ? 'true' : 'false'"
            :class="{ 'is-chosen': isChosen(option) }"
            @click="choose(option)"
          >
            <!--
              The campaign's own picture, or the icon the influencer dashboard
              shows for a campaign that has none. It doubles as the chosen mark:
              a tick over the picture says what a separate checkbox beside it
              would have said, and one thing per row is enough.
            -->
            <span class="campaign-picker__thumb" aria-hidden="true">
              <img
                v-if="option.image"
                :src="option.image"
                alt=""
                loading="lazy"
                @error="option.image = ''"
              >
              <i v-else class="fa-solid fa-photo-film"></i>
              <i class="fa-solid fa-check campaign-picker__tick"></i>
            </span>
            <span class="campaign-picker__label">{{ option.name }}</span>
          </button>
        </li>
      </ul>

      <footer class="campaign-picker__foot">
        <!--
          Clearing belongs among the options rather than behind a control of its
          own, and stays put at the bottom instead of scrolling away with the
          first page.
        -->
        <button type="button" class="campaign-picker__clear" @click="clear">
          {{ multiple ? 'Clear' : 'None — copy only' }}
        </button>

        <span v-if="campaigns.loading && campaigns.items.length">Loading more&hellip;</span>
        <template v-else-if="campaigns.needsManualLoad">
          <span v-if="campaigns.error" class="campaign-picker__error">{{ campaigns.error }}</span>
          <button type="button" class="campaign-picker__more" @click="campaigns.loadMore">
            {{ campaigns.error ? 'Try again' : 'Load more' }}
          </button>
        </template>
        <span v-else-if="campaigns.cursor">Scroll for more</span>
      </footer>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { usePagedList, requestJson } from './paged-list.mjs'

/**
 * A client's own challenges, searched and paged - as one choice for the
 * "link a campaign" field, or as many for the "applied to campaign" filter.
 *
 * One component for both, because they are one widget: the same rows, the same
 * endpoint, the same panel. Only the arity differs, and that is a prop.
 *
 * Neither list is bounded, which is why neither is a <select>. Both used to be:
 * the field was filled by a `limit(200)` on the server, so a client with more
 * campaigns than that simply could not reach their oldest ones, and nothing on
 * the page said so.
 */
export default {
  name: 'CampaignPicker',

  props: {
    // Endpoint returning { items: [{ id, name }], next_cursor }, and resolving
    // ids to names when asked with ?ids=.
    url: {
      type: String,
      required: true
    },
    // A campaign id as a string, or an array of them when multiple.
    modelValue: {
      type: [String, Array],
      required: false,
      default: ''
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false
    },
    // Submits its value under this name when set. A filter row carries its
    // value in the builder's payload instead, and passes nothing here.
    name: {
      type: String,
      required: false,
      default: ''
    },
    // What the trigger says with nothing chosen.
    placeholder: {
      type: String,
      required: false,
      default: 'None'
    },
    label: {
      type: String,
      required: false,
      default: 'Choose a campaign'
    }
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const campaigns = usePagedList({ url: props.url })
    const open = ref(false)
    const root = ref(null)
    const trigger = ref(null)
    const search = ref(null)

    // id -> name for whatever is currently chosen, so the closed trigger can
    // name it. Filled from the rows as they are clicked, and resolved from the
    // server once at load for a selection restored from a saved draft - those
    // ids may sit on any page, and none of them are loaded yet.
    const names = reactive({})

    /** The name lookup in flight, so it can be dropped with the component. */
    let namesRequest = null

    /** The chosen ids, always as an array of strings whatever the arity. */
    const chosen = computed(() => (
      props.multiple
        ? (props.modelValue || []).map(String)
        : (props.modelValue ? [String(props.modelValue)] : [])
    ))

    const summary = computed(() => {
      if (!chosen.value.length) {
        return props.placeholder
      }

      if (chosen.value.length === 1) {
        return names[chosen.value[0]] || 'Loading…'
      }

      return `${chosen.value.length} campaigns`
    })

    function emitChosen(ids) {
      emit('update:modelValue', props.multiple ? ids : (ids[0] || ''))
    }

    function isChosen(option) {
      return chosen.value.includes(String(option.id))
    }

    function choose(option) {
      const id = String(option.id)
      names[id] = option.name

      if (!props.multiple) {
        emitChosen([id])
        close()

        return
      }

      // Ticking the same row again unticks it: in a list of checkboxes that is
      // the only thing a second click can mean.
      emitChosen(isChosen(option) ? chosen.value.filter((held) => held !== id) : chosen.value.concat(id))
    }

    function clear() {
      emitChosen([])

      if (!props.multiple) {
        close()
      }
    }

    // This picker's own element, not any picker's: the message step can hold
    // two of these, and closest('.campaign-picker') would let a click on one
    // count as a click inside the other.
    function closeOnOutside(event) {
      if (!root.value?.contains(event.target)) {
        close()
      }
    }

    function closeOnEscape(event) {
      if (event.key === 'Escape') {
        close()
        // Back to the control that opened it, or focus is left on an element
        // that no longer exists and the page jumps to the top.
        trigger.value?.focus()
      }
    }

    function openPanel() {
      open.value = true

      // Bound only while the panel is open: a document-wide handler left in
      // place would run on every click on the page for the rest of the session.
      document.addEventListener('click', closeOnOutside, true)
      document.addEventListener('keydown', closeOnEscape)

      // Asked again on every opening rather than cached: a client who just
      // created a campaign in another tab should find it here.
      campaigns.reload()
      nextTick(() => search.value?.focus())
    }

    function close() {
      open.value = false
      campaigns.destroy()
      detach()
    }

    function toggle() {
      open.value ? close() : openPanel()
    }

    /**
     * Name what is already chosen.
     *
     * Only for a single selection, because that is the only thing a name is
     * ever shown for - `summary` names one choice and counts any more than
     * that. Tying the lookup to the display rather than to the selection keeps
     * the endpoint's one-page cap on `ids` honest: it can never hide a name,
     * because it is never asked for more than one.
     *
     * A filter row can hold more campaigns than that cap - nothing limits how
     * many a client ticks - so without this the lookup would fetch fifty names
     * to display none of them, and a large enough selection would build a URL
     * long enough to be refused.
     */
    async function resolveNames() {
      const missing = chosen.value.length === 1 && !names[chosen.value[0]]
        ? [chosen.value[0]]
        : []

      if (!missing.length) {
        return
      }

      namesRequest?.abort()
      namesRequest = new AbortController()

      try {
        const data = await requestJson(props.url, 'GET', { ids: missing.join(',') }, namesRequest.signal)

        data.items.forEach((item) => {
          names[String(item.id)] = item.name
        })
      } catch (e) {
        if (e.name === 'AbortError') {
          return
        }

        // A name that cannot be fetched is a cosmetic loss: the id is still
        // held, still submitted and still valid. Saying "Loading…" forever
        // would be worse than saying what it is.
        missing.forEach((id) => {
          names[id] = `Campaign #${id}`
        })
      }
    }

    // Runs now for a selection restored from a draft, and again whenever the
    // held ids change from outside this component.
    watch(chosen, resolveNames, { immediate: true })

    /**
     * What this component started and the list controller cannot know about:
     * two document listeners and the name lookup.
     *
     * Not `close()`. That also tears the controller down, and the controller
     * already does that for itself when the component unmounts - calling both
     * would be two teardowns for one component.
     */
    function detach() {
      namesRequest?.abort()
      document.removeEventListener('click', closeOnOutside, true)
      document.removeEventListener('keydown', closeOnEscape)
    }

    onBeforeUnmount(detach)

    return { campaigns, open, root, trigger, search, chosen, summary, isChosen, choose, clear, toggle }
  }
}
</script>
